import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations, getDirection, languageNames, languageFlags } from '../i18n';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  // Default to Arabic as per user request
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('gategroup_language');
    return saved || 'ar';
  });

  const [direction, setDirection] = useState(() => getDirection('ar'));

  useEffect(() => {
    localStorage.setItem('gategroup_language', language);
    const dir = getDirection(language);
    setDirection(dir);
    
    // Update document direction and language
    document.documentElement.dir = dir;
    document.documentElement.lang = language;
    
    // Add/remove RTL class for styling
    if (dir === 'rtl') {
      document.body.classList.add('rtl');
      document.body.classList.remove('ltr');
    } else {
      document.body.classList.add('ltr');
      document.body.classList.remove('rtl');
    }
  }, [language]);

  // Get translation function with variable interpolation
  const t = (key, vars) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        // Fallback to English if key not found
        value = undefined;
        break;
      }
    }
    
    // Fallback to English translation if not found
    if (value === undefined) {
      let fallback = translations['en'];
      for (const k of keys) {
        if (fallback && typeof fallback === 'object') {
          fallback = fallback[k];
        } else {
          return key; // Return key if not found in any language
        }
      }
      value = fallback;
    }

    if (typeof value === 'string' && vars && typeof vars === 'object') {
      return value.replace(/\{(\w+)\}/g, (m, k) =>
        Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : m
      );
    }
    return value === undefined ? key : value;
  };

  // Change language function
  const changeLanguage = (newLang) => {
    if (translations[newLang]) {
      setLanguage(newLang);
    }
  };

  // Available languages
  const availableLanguages = Object.keys(translations).map(code => ({
    code,
    name: languageNames[code],
    flag: languageFlags[code],
  }));

  const value = {
    language,
    direction,
    t,
    changeLanguage,
    availableLanguages,
    isRTL: direction === 'rtl',
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
